import * as React from "react";
import { motion, useReducedMotion } from "motion/react";
import { useQueryClient } from "@tanstack/react-query";
import { Bell, MessageCircleHeart } from "lucide-react";
import { cn } from "@eduviaos/ui-kit";
import { verticalLabel, type Institution, type LearnerPortalRole } from "../lib/institution";
import { ageBandFromClass } from "../lib/ziggy";
import { guardianNav, localizedStudyCentreNav, studentNav } from "../lib/vertical";
import { usePortalLocale } from "../lib/i18n/usePortalLocale";
import { useSession } from "../lib/sessionContext";
import { BottomNav } from "../components/BottomNav";
import { SideNav } from "../components/SideNav";
import { InstitutionMark } from "../components/InstitutionMark";
import { AskSheet } from "../components/AskSheet";
import { ZiggyChooser } from "../components/ZiggyChooser";
import { ZiggyServerSync } from "../lib/useZiggyPersistence";
import { StudentHome } from "./StudentHome";
import { ParentHome } from "./ParentHome";
import { CoachingHome } from "./student/CoachingHome";
import { StudyCentreHome } from "./student/StudyCentreHome";
import { TestsPanel } from "./coaching/TestsPanel";
import { SeatPanel } from "./sc/SeatPanel";
import { LearningHub } from "./sc/LearningHub";
import { StudyGroupsHub } from "./sc/StudyGroupsHub";
import { SupportPanel } from "./sc/SupportPanel";
import type { LearnHubAction } from "../lib/learning/types";
import { COACH_KEY, useNotifications } from "../lib/learning/hooks";
import { NoticesPanel, ProfilePanel, SchedulePanel } from "./SubPanels";
import { LEARNER } from "../lib/mockData";
import { useMyEnrollment } from "../lib/enrollment/api";
import { showsSeatTab, useMySeat } from "../lib/seat/api";
import { TeamApp } from "./team/TeamApp";

const EASE_OUT: [number, number, number, number] = [0.23, 1, 0.32, 1];

export function HomeScreen({ institution }: { institution: Institution }) {
  const { user } = useSession();
  if (user?.role === "team") return <TeamApp institution={institution} />;
  return <LearnerHome institution={institution} role={user?.role ?? "student"} />;
}

function LearnerHome({ institution, role }: { institution: Institution; role: LearnerPortalRole }) {
  const reduce = useReducedMotion();
  const qc = useQueryClient();
  const { user } = useSession();
  const [tab, setTab] = React.useState("home");
  const [learnAction, setLearnAction] = React.useState<LearnHubAction | null>(null);
  const [askOpen, setAskOpen] = React.useState(false);
  const [pendingGroupInvite, setPendingGroupInvite] = React.useState<string | null>(null);

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get("tab");
    const invite = params.get("invite");
    if (tabParam) setTab(tabParam);
    if (invite) {
      setTab("groups");
      setPendingGroupInvite(invite);
      const url = new URL(window.location.href);
      url.searchParams.delete("invite");
      url.searchParams.delete("tab");
      window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
    }
  }, []);

  const firstName = (user?.name ?? "there").split(" ")[0] ?? "there";
  const childName = user?.children[0] ?? "Aarav Sharma";
  const isParent = role === "guardian";
  const vertical = institution.vertical;
  const isStudyCentreLearner = !isParent && vertical === "study_centre";
  const portalLocale = usePortalLocale(isStudyCentreLearner && institution.learnerAppOffered);
  const seatQuery = useMySeat(isStudyCentreLearner);
  const hideSeatTab = seatQuery.isSuccess && !showsSeatTab(seatQuery.data);
  const baseTabs = isParent
    ? guardianNav()
    : vertical === "study_centre"
      ? localizedStudyCentreNav(portalLocale.strings, institution.learnerAppOffered)
      : studentNav(vertical, institution.learnerAppOffered);
  const tabs =
    hideSeatTab && vertical === "study_centre"
      ? baseTabs.filter((item) => item.id !== "seat")
      : baseTabs;
  const noticeTab = isParent ? "updates" : "notices";
  const showPrototypeChrome = !isStudyCentreLearner;
  const showMobileNotifications = !isParent;
  const enrollmentQuery = useMyEnrollment(isStudyCentreLearner && institution.learnerAppOffered);
  const needsAppPayment = enrollmentQuery.data?.needs_app_payment ?? false;

  // The bell used to paint its dot unconditionally, so learners with an empty
  // Notices list still saw "you have something waiting" on every screen and
  // reading the list never cleared it. Only the real unread count decides.
  const usesLearningNotices = isStudyCentreLearner && institution.learnerAppOffered;
  const noticesQuery = useNotifications(usesLearningNotices);
  const unreadNotices = usesLearningNotices ? (noticesQuery.data?.unread_count ?? 0) : undefined;

  // Study-centre learners who still need intake land on Learn — the home
  // dashboard has nothing honest to show until onboarding completes
  // (Iron Rule #2: no fabricated progress). SC-5 uses enrollment scope,
  // not raw pathway count, so a half-started plan still routes to intake.
  // SC-4: skip intake redirect when app access is blocked by paywall.
  React.useEffect(() => {
    if (!isStudyCentreLearner || !institution.learnerAppOffered) return;
    if (enrollmentQuery.isLoading || enrollmentQuery.isError) return;
    if (needsAppPayment) return;
    if (enrollmentQuery.data?.needs_intake && tab === "home") {
      setTab("revise");
    }
  }, [
    isStudyCentreLearner,
    institution.learnerAppOffered,
    enrollmentQuery.isLoading,
    enrollmentQuery.isError,
    enrollmentQuery.data?.needs_intake,
    needsAppPayment,
    tab,
  ]);

  React.useEffect(() => {
    if (!isStudyCentreLearner || tab !== "home") return;
    void qc.invalidateQueries({ queryKey: COACH_KEY.summary });
  }, [isStudyCentreLearner, tab, qc]);

  // Guardians always meet grown-up Ziggy; study-centre learners are
  // self-directed (no class on record). School/coaching still use the
  // prototype fixture until class metadata ships on `/auth/me`.
  const ziggyAge =
    isParent || vertical === "study_centre" ? "grown" : ageBandFromClass(LEARNER.className);

  function select(next: string) {
    setTab(next);
    if (next !== "groups") {
      window.scrollTo({ top: 0, behavior: reduce ? "auto" : "smooth" });
    }
  }

  function openLearn(action: LearnHubAction | null = null) {
    setLearnAction(action);
    select("revise");
  }

  function renderStudentHome() {
    if (vertical === "coaching_institute") {
      return <CoachingHome firstName={firstName} ziggyAge={ziggyAge} />;
    }
    if (vertical === "study_centre") {
      return (
        <StudyCentreHome
          firstName={firstName}
          ziggyAge={ziggyAge}
          onOpenRevise={openLearn}
          onOpenSeat={() => select("seat")}
        />
      );
    }
    return <StudentHome firstName={firstName} ziggyAge={ziggyAge} />;
  }

  return (
    <div
      className={cn(
        "portal-aurora bg-background text-foreground relative flex lg:flex",
        isStudyCentreLearner && tab === "groups" ? "h-dvh max-h-dvh overflow-hidden" : "min-h-dvh",
      )}
    >
      <SideNav
        institution={institution}
        items={tabs}
        activeId={tab}
        onSelect={select}
        onAskZiggy={showPrototypeChrome ? () => setAskOpen(true) : undefined}
        onNotifications={showMobileNotifications ? () => select(noticeTab) : undefined}
        unreadNotifications={unreadNotices}
      />

      <div
        className={cn(
          "flex min-w-0 flex-1 flex-col",
          isStudyCentreLearner && tab === "groups" && "min-h-0 overflow-hidden",
        )}
      >
        {/* Mobile header (sidebar replaces it on desktop) */}
        <header className="border-border/50 bg-background/80 sticky top-0 z-20 border-b backdrop-blur-lg lg:hidden">
          <div className="mx-auto flex max-w-md items-center justify-between px-4 py-3 sm:max-w-xl md:max-w-3xl lg:max-w-5xl">
            <div className="flex items-center gap-2.5">
              <InstitutionMark vertical={institution.vertical} name={institution.name} size="sm" />
              <div className="leading-tight">
                <p className="text-sm font-semibold">{institution.shortName}</p>
                <p className="text-muted-foreground text-[0.7rem]">
                  {verticalLabel(institution.vertical)}
                </p>
              </div>
            </div>
            {showMobileNotifications && (
              <button
                type="button"
                onClick={() => select(noticeTab)}
                aria-label={
                  unreadNotices
                    ? portalLocale.strings.notificationsWithUnread(unreadNotices)
                    : portalLocale.strings.notifications
                }
                className="text-muted-foreground hover:text-foreground relative grid size-9 place-items-center rounded-full transition-colors active:scale-95"
              >
                <Bell className="size-5" />
                {(unreadNotices === undefined || unreadNotices > 0) && (
                  <span className="bg-celebrate ring-background absolute right-1.5 top-1.5 size-2 rounded-full ring-2" />
                )}
              </button>
            )}
          </div>
        </header>

        {/* Content */}
        <main
          className={
            isStudyCentreLearner && tab === "groups"
              ? "mx-auto flex min-h-0 w-full flex-1 flex-col overflow-hidden px-4 pb-24 pt-5 lg:max-w-none lg:px-6 lg:pb-6 lg:pt-6 xl:max-w-[90rem]"
              : isStudyCentreLearner
                ? "mx-auto w-full max-w-md px-4 pb-28 pt-5 sm:max-w-xl md:max-w-3xl md:px-6 lg:max-w-5xl lg:px-8 lg:pb-12 lg:pt-8 xl:max-w-6xl"
                : "mx-auto w-full max-w-md px-4 pb-28 pt-5 lg:max-w-5xl lg:px-8 lg:pb-12 lg:pt-8"
          }
        >
          <motion.div
            key={tab}
            initial={reduce ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: EASE_OUT }}
            className={
              tab === "groups" && isStudyCentreLearner
                ? "flex min-h-0 flex-1 flex-col"
                : tab === "home" || (isStudyCentreLearner && tab === "revise")
                  ? undefined
                  : "lg:mx-auto lg:max-w-2xl"
            }
          >
            {tab === "home" &&
              (isParent ? (
                <ParentHome firstName={firstName} childName={childName} />
              ) : (
                renderStudentHome()
              ))}
            {(tab === "schedule" || tab === "calendar") && <SchedulePanel />}
            {tab === "tests" && <TestsPanel />}
            {tab === "seat" && <SeatPanel />}
            {tab === "revise" && (
              <LearningHub
                ziggyAge={ziggyAge}
                centreName={institution.shortName}
                initialAction={learnAction}
                onInitialActionConsumed={() => setLearnAction(null)}
              />
            )}
            {tab === "groups" && isStudyCentreLearner && (
              <StudyGroupsHub
                pendingInvite={pendingGroupInvite}
                onInviteConsumed={() => setPendingGroupInvite(null)}
              />
            )}
            {tab === "support" && isStudyCentreLearner && (
              <SupportPanel learnerName={user?.name ?? "Learner"} />
            )}
            {(tab === "notices" || tab === "updates") && (
              <NoticesPanel
                useLearningApi={isStudyCentreLearner && institution.learnerAppOffered}
              />
            )}
            {tab === "profile" && <ProfilePanel institution={institution} ziggyAge={ziggyAge} />}
          </motion.div>
        </main>
      </div>

      {/* Mobile floating nav */}
      <div className="lg:hidden">
        <BottomNav
          items={tabs}
          activeId={tab}
          onSelect={select}
          fab={
            showPrototypeChrome
              ? {
                  label: "Ask Ziggy",
                  icon: MessageCircleHeart,
                  onClick: () => setAskOpen(true),
                }
              : undefined
          }
        />
      </div>

      {showPrototypeChrome && (
        <AskSheet
          open={askOpen}
          onClose={() => setAskOpen(false)}
          role={role}
          ziggyAge={ziggyAge}
        />
      )}
      {!isParent && <ZiggyChooser age={ziggyAge} />}
      {!isParent && <ZiggyServerSync />}
    </div>
  );
}
